import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import SiteDetailPageTabs from './SiteDetailPageTabs';
import { redirectToSiteVisitNew } from '../../../utils/navigation';
import { getTabsOrder, updateTabsOrder } from '../../../utils/api/site';
import { useToasts } from '../../../hooks';
import { capitalizeFirstLetter } from '../../../utils/api';
import { reorder } from '../../../utils/dragDrop';
import { useUrlModals } from '../../../utils/useUrlModals';

const MODAL_CONFIG = {
  addNote: { isIdParam: false },
};

const SiteDetailPageTabsWrapper = ({
  siteData, setSiteData, currentUserId, currentUserRole,
}) => {
  const [tabsOrder, setTabsOrder] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const { showToast } = useToasts();

  // URL-based modal management
  const { modals, openModal, closeModal } = useUrlModals(MODAL_CONFIG);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOpenAddNoteModal = () => {
    openModal('addNote');
  };

  // Cleans up the URL when closing modal
  const handleCloseAddNoteModal = () => {
    closeModal('addNote');
  };

  useEffect(() => {
    if (!siteData.organization_id) return;
    getTabsOrder(siteData.organization_id)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((data) => {
        setTabsOrder(data.organization?.app_data?.site?.tab_order);
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteData.organization_id]);

  // wrap the function in useCallback to prevent it from being recreated on every render(because we are using them in useMemo)
  const addVisit = useCallback((e) => {
    e.preventDefault();
    redirectToSiteVisitNew(siteData.id, 'visits', true);
  }, [siteData.id]);

  const tabsData = useMemo(() => {
    return tabsOrder?.map((tab) => {
      const tabData = {
        title: tab.split(' ').map(word => capitalizeFirstLetter(word)).join(' '),
        data: siteData[tab.toLowerCase()],
        site_id: siteData.id,
        organization_id: siteData.organization_id,
        length: siteData[tab.toLowerCase()]?.length,
      };

      if (tab === 'visits') {
        tabData.onClick = addVisit;
      }
      if (tab === 'notes') {
        tabData.onClick = handleOpenAddNoteModal;
      }
      if (tab === 'contact history') {
        tabData.data = siteData.contact_histories;
        tabData.length = siteData.contact_histories?.total_count;
      }
      if (tab === 'site history') {
        tabData.data = siteData.site_histories;
        tabData.length = siteData.site_histories?.total_count;
      }
      return tabData;
    });
  }, [siteData, tabsOrder, addVisit, handleOpenAddNoteModal]);

  const onDragEnd = (result) => {
    // dropped outside the list
    // if item is dropped in same position
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const prevTabsOrder = tabsOrder;

    const reorderedTabs = reorder(
      tabsOrder,
      result.source.index,
      result.destination.index,
    );

    setTabsOrder(reorderedTabs);
    updateTabsOrder(siteData.organization_id, reorderedTabs)
      .catch((error) => {
        setTabsOrder(prevTabsOrder);
        showToast({ message: error.message, type: 'error' });
      });
  };

  if (!tabsOrder?.length) return null;
  return (
    <>
      <SiteDetailPageTabs
        tabsData={tabsData}
        onDragEnd={onDragEnd}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isOpenAddEditNoteModal={modals.addNote?.isOpen || false}
        setOpenAddNoteModal={handleCloseAddNoteModal}
        organizationId={siteData.organization_id}
        siteId={siteData.id}
        siteName={siteData.name}
        showToast={showToast}
        setSiteData={setSiteData}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
      />
    </>
  );
};

SiteDetailPageTabsWrapper.propTypes = {
  siteData: PropTypes.object.isRequired,
  setSiteData: PropTypes.func.isRequired,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
};

export default SiteDetailPageTabsWrapper;
